import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EmailSchema, type MeResponse, type PasswordLoginBody } from '../../../src/shared/auth';
import {
  authKeys,
  fetchMe,
  fetchPasswordStatus,
  passwordLogin,
  passwordStatusKey,
} from '../../api/auth';
import { TurnstileWidget } from '../../components/TurnstileWidget';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingScreen } from '../../components/LoadingScreen';

/**
 * `/admin/login` — platform-admin password sign-in (migration 0006
 * `user_credentials`). Independent of the email-OTP flow so the admin console
 * stays reachable when the email provider is down. While the one-time
 * super-user setup is still open (`GET /api/auth/password/status`), the page
 * forwards to `/admin/setup` instead.
 */

/** The public site key is build-time client config; read lazily so tests can stub env. */
function turnstileSiteKey(): string {
  return import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';
}

/** Read the path a route guard bounced us from, if any (`location.state.from`). */
function readFromLocationState(state: unknown): string | null {
  if (typeof state === 'object' && state !== null && 'from' in state) {
    const from: unknown = state.from;
    if (typeof from === 'string' && from.startsWith('/')) {
      return from;
    }
  }
  return null;
}

export function AdminLoginPage(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const from = readFromLocationState(location.state);

  const status = useQuery({
    queryKey: passwordStatusKey,
    queryFn: ({ signal }) => fetchPasswordStatus(signal),
  });

  const loginMutation = useMutation({
    mutationFn: async (body: PasswordLoginBody): Promise<MeResponse> => {
      await passwordLogin(body);
      return fetchMe();
    },
    onSuccess: (me) => {
      queryClient.setQueryData(authKeys.me, me);
      void navigate(from ?? '/admin', { replace: true });
    },
  });

  if (status.isPending) {
    return <LoadingScreen />;
  }
  if (status.isSuccess && status.data.setupRequired) {
    return <Navigate to="/admin/setup" replace />;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsedEmail = EmailSchema.safeParse(email);
    if (!parsedEmail.success) {
      setFieldError(parsedEmail.error.issues[0]?.message ?? 'Enter a valid email address.');
      return;
    }
    if (password.length === 0) {
      setFieldError('Enter your password.');
      return;
    }
    setFieldError(null);
    if (turnstileToken === null) {
      return;
    }
    loginMutation.mutate({ email: parsedEmail.data, password, turnstileToken });
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-md">
        <h1 className="text-3xl font-semibold tracking-tight">Platform admin</h1>
        <p className="mt-2 text-slate-600">Super-user console for Astralis FrontDesk.</p>

        <section className="mt-8 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-medium">Sign in with password</h2>
          <form className="mt-4 space-y-4" onSubmit={handleSubmit} noValidate>
            <div>
              <label htmlFor="admin-email" className="block text-sm font-medium text-slate-700">
                Email
              </label>
              <input
                id="admin-email"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
              />
            </div>

            <div>
              <label htmlFor="admin-password" className="block text-sm font-medium text-slate-700">
                Password
              </label>
              <input
                id="admin-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
              />
            </div>

            <TurnstileWidget siteKey={turnstileSiteKey()} onToken={setTurnstileToken} />

            {fieldError !== null && <p className="text-sm text-red-600">{fieldError}</p>}
            {status.isError && <ErrorNotice error={status.error} />}
            {loginMutation.isError && <ErrorNotice error={loginMutation.error} />}

            <button
              type="submit"
              disabled={turnstileToken === null || loginMutation.isPending}
              className="w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loginMutation.isPending ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="mt-4 text-sm text-slate-600">
            Not a platform admin?{' '}
            <a href="/login" className="font-medium text-slate-900 underline underline-offset-4">
              Sign in with an email code
            </a>
          </p>
        </section>
      </div>
    </main>
  );
}
